import { useEffect, useMemo } from 'react';

import { CROP_DEFAULT_PRODUCT_TYPE } from 'appConstants';
import { FormikProvider, useField, useFormik } from 'formik';
import { calculateCropQuantityFromPacks } from 'utils';

import useValidationSchema from './useValidationSchema';

export default function ProductFormFields({ onSubmit, children }) {
  const [{ value: products }] = useField('products');
  const indexedProducts = useMemo(
    () =>
      products.reduce(
        (acc, cur) => ({ ...acc, [cur.packagedItemReference]: cur.packagedItemQty }),
        {},
      ),
    [products],
  );

  const productSchema = useValidationSchema(indexedProducts);

  const initialValues = useMemo(() => productSchema.cast({}, { assert: false }), [productSchema]);

  const formik = useFormik({
    enableReinitialize: true,
    validateOnMount: true,
    initialValues,
    validationSchema: productSchema,
    onSubmit: (data: any, { resetForm, validateForm }) => {
      const { productType = CROP_DEFAULT_PRODUCT_TYPE, packagedItemQty, productData } = data; // Keep selected product type

      const normalizedQuantity =
        calculateCropQuantityFromPacks(Number(packagedItemQty), { productData }) ||
        Number(data.quantity);

      onSubmit(
        {
          ...data,
          packagedItemQty: Number(packagedItemQty),
          quantity: Number.isFinite(normalizedQuantity) ? normalizedQuantity : undefined,
        },
        async () => {
          resetForm({
            touched: {},
            values: productSchema.cast({ productType }, { assert: false }),
          });
          await validateForm(initialValues);
        },
      );
    },
  });

  const {
    values: { packagedItemReference },
    validateForm,
  } = formik;

  // Validate max number of packs
  useEffect(() => {
    if (indexedProducts && packagedItemReference && indexedProducts[packagedItemReference]) {
      validateForm();
    }
  }, [validateForm, indexedProducts, packagedItemReference]);

  return (
    <FormikProvider value={formik}>
      <form autoComplete="off" onSubmit={formik.handleSubmit}>
        {children}
      </form>
    </FormikProvider>
  );
}
